import React, { useEffect, useState } from "react";
import { MdAdd } from "react-icons/md";
import isEqual from "lodash.isequal";
import { FiMinus } from "react-icons/fi";
import { FaWhatsapp, FaFacebookF, FaTwitter, FaLink, FaRegHeart, FaHeart } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ProductRatings from "./ratings/ProductRatings";
import { Btnoutline, StockBtn } from "../basic/button";
import { useDispatch } from "react-redux";
import { addCartItems, sendNotifyRequest } from "../../../redux/slices/addToCart";
import { toast } from "react-toastify";
import { fetchUserWishlists, createWishlist, addToWishlist, updateWishlistQuantity } from "../../../redux/slices/wishlist";

const checkCombinationMatch = (combination, selectedValuesMap, variantNameMapping) => {
    if (!combination?.combination_attributes || !selectedValuesMap) return false;
    if (Object.keys(selectedValuesMap).length !== combination.combination_attributes.length) return false;

    return combination.combination_attributes.every(comboAttr => {
        const attributeName = variantNameMapping[comboAttr.attribute_id]?.variant;
        if (!attributeName) return false;
        const selectedValueForAttr = selectedValuesMap[attributeName]?.value;
        return selectedValueForAttr === comboAttr.value;
    });
};

const ProductSingleContentPage = ({ oneproduct, singleProductIsLoading, singleProductError, localCartItems }) => {

    console.log('localCartItems',localCartItems)
    const linkToShare = `${process.env.REACT_PUBLIC_APP_URL || 'http://localhost:3000'}/shop`;
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [selectedAttributeValues, setSelectedAttributeValues] = useState({});
    const [quantity, setQuantity] = useState(1);
    const [matchedCombination, setMatchedCombination] = useState(null);
    const [displayPrice, setDisplayPrice] = useState(0);
    const [displayDiscountPrice, setDisplayDiscountPrice] = useState(0);
    const [isProductOutOfStock, setIsProductOutOfStock] = useState(false);
    const [isInCart, setIsInCart] = useState(false);
    const [loading, setLoading] = useState(true);
    const [wishlist, setWishlist] = useState(new Set(JSON.parse(localStorage.getItem("wishlist")) || []));
    const [showWishlistModalFor, setShowWishlistModalFor] = useState(null);
    const [userWishlists, setUserWishlists] = useState({});
    const [newListName, setNewListName] = useState("");
    const [showHurryMessage, setShowHurryMessage] = useState(false);

    const {
        id,
        name,
        description,
        variant_name = {},
        variant_value = [],
        variant_combinations = [],
    } = oneproduct;

    useEffect(() => {
      const stockQty = matchedCombination
        ? matchedCombination.stock_quantity
        : oneproduct.stock_quantity;
    
      if (stockQty && stockQty <= 5 && stockQty > 0) {
        setShowHurryMessage(true);
      } else {
        setShowHurryMessage(false);
      }
    }, [matchedCombination, oneproduct]);

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!id) return;
        const cartQuantities = JSON.parse(localStorage.getItem("cart_quantities")) || {};
        const storedQty = cartQuantities[id];
        if (storedQty) setQuantity(storedQty);
    }, [id]);

    useEffect(() => {
        const loadWishlists = async () => {
            const user_id = JSON.parse(localStorage.getItem("user"))?.id;
            if (!user_id) return;

            const response = await dispatch(fetchUserWishlists(user_id, toast));
            if (response?.success && response.data) {
                const wishlistsObj = {};
                for (const list of response.data) {
                    wishlistsObj[list.name || "Default"] = {
                        id: list.id,
                        items: list.items || [],
                    };
                }
                setUserWishlists(wishlistsObj);
            }
        };

        loadWishlists();
    }, [dispatch]);

    useEffect(() => {
        if (!oneproduct || singleProductIsLoading || singleProductError) return;

        if (oneproduct.is_variant === "true" && variant_combinations.length > 0) {
            const defaultCombination = variant_combinations.find(
                combo => combo.stock_quantity > 0 && combo.stock_status === "in_stock"
            ) || variant_combinations[0];

            if (defaultCombination) {
                setMatchedCombination(defaultCombination);
                const initialSelected = {};
                defaultCombination.combination_attributes.forEach(attr => {
                    const attributeName = variant_name[attr.attribute_id]?.variant;
                    const variantType = variant_value.find(vt => vt.id.toString() === attr.attribute_id.toString());
                    const fullValueObject = variantType?.multiple_values.find(mv => mv.value === attr.value);

                    if (attributeName && fullValueObject) {
                        initialSelected[attributeName] = { id: fullValueObject.id, value: fullValueObject.value };
                    }
                });
                setSelectedAttributeValues(initialSelected);
            } else {
                setMatchedCombination(null);
                setIsProductOutOfStock(true);
                setDisplayPrice(0);
                setDisplayDiscountPrice(0);
            }
        } else if (oneproduct.is_variant === "false") {
            setDisplayPrice(parseFloat(oneproduct.price || 0));
            setDisplayDiscountPrice(parseFloat(oneproduct.discount_price || oneproduct.price || 0));
            setIsProductOutOfStock(oneproduct.stock_quantity === 0 || oneproduct.stock_status === "out_of_stock");
            setMatchedCombination(null);
        }
    }, [oneproduct, singleProductIsLoading, singleProductError, variant_combinations, variant_name, variant_value]);

    useEffect(() => {
        if (oneproduct.is_variant === "true" && Object.keys(selectedAttributeValues).length > 0 && variant_combinations.length > 0) {
            const foundCombination = variant_combinations.find(combo =>
                checkCombinationMatch(combo, selectedAttributeValues, variant_name)
            );
            setMatchedCombination(foundCombination);

            const allRequiredAttributesSelected = Object.values(variant_name).every(
                (vn) => selectedAttributeValues[vn.variant] !== undefined
            );
        } else if (oneproduct.is_variant === "false") {
            setMatchedCombination(null);
        }
    }, [selectedAttributeValues, variant_combinations, oneproduct.is_variant, variant_name]);

    useEffect(() => {
        if (matchedCombination) {
            setDisplayPrice(parseFloat(matchedCombination.price || 0));
            setDisplayDiscountPrice(parseFloat(matchedCombination.discount_price || matchedCombination.price || 0));
            setIsProductOutOfStock(matchedCombination.stock_quantity === 0 || matchedCombination.stock_status === "out_of_stock");
        } else if (oneproduct.is_variant === "false") {
            setDisplayPrice(parseFloat(oneproduct.price || 0));
            setDisplayDiscountPrice(parseFloat(oneproduct.discount_price || oneproduct.price || 0));
            setIsProductOutOfStock(oneproduct.stock_quantity === 0 || oneproduct.stock_status === "out_of_stock");
        } else {
            setDisplayPrice(0);
            setDisplayDiscountPrice(0);
            setIsProductOutOfStock(true);
        }
    }, [matchedCombination, oneproduct]);

    const handleIncrement = () => {
        const maxQty = matchedCombination
            ? matchedCombination.stock_quantity
            : oneproduct.stock_quantity;
    
        setQuantity(prev => {
            if (prev < maxQty) {
                return prev + 1;
            } else {
                toast.warning(`Only ${maxQty} item(s) available in stock.`);
                return prev;
            }
        });
    };

    const handleDecrement = () => setQuantity(prev => (prev > 1 ? prev - 1 : 1));

    const handleSelectValue = (variantAttributeName, selectedValueObject) => {
        setSelectedAttributeValues(prev => ({
            ...prev,
            [variantAttributeName]: selectedValueObject,
        }));
    };

    useEffect(() => {
        const match = localCartItems.some(item => {
            if (oneproduct.is_variant === "true" && matchedCombination) {
                return item.item_id === oneproduct.id && item.variant_id === matchedCombination.id;
            }
            return item.item_id === oneproduct.id && !item.variant_id;
        });
        setIsInCart(match);
    }, [matchedCombination, localCartItems, oneproduct.id, oneproduct.is_variant]);

    const transformSelectedVariants = (selectedVals) => {
        return Object.entries(selectedVals).map(([attribute, { value }]) => ({
            attribute,
            value
        }));
    };

    const handleAddToCart = () => {
        if (oneproduct.is_variant === "true" && (!matchedCombination )) {
            toast.error("Please select all valid variant options.");
            return;
        }

        const cart_id = localStorage.getItem("cart_id") ? Number(localStorage.getItem("cart_id")) : null;
        const customer_id = JSON.parse(localStorage.getItem("user"))?.id || null;

        const baseCartItem = {
            item_id: oneproduct.id,
            quantity,
            price: parseFloat(matchedCombination?.price || oneproduct.price),
            discount_price: parseFloat(matchedCombination?.discount_price || matchedCombination?.price || oneproduct.discount_price || oneproduct.price),
            ...(cart_id && { cart_id }),
            ...(customer_id && { customer_id }),
        };

        const cartItem = oneproduct.is_variant === "true" && matchedCombination
            ? {
                ...baseCartItem,
                variant_id: matchedCombination.id,
                selectedVariants: transformSelectedVariants(selectedAttributeValues)
            }
            : { ...baseCartItem, variant_id: null, selectedVariants: [] };

        dispatch(addCartItems(cartItem, toast, navigate));

        const cartQuantities = JSON.parse(localStorage.getItem("cart_quantities")) || {};
        cartQuantities[oneproduct.id] = quantity;
        localStorage.setItem("cart_quantities", JSON.stringify(cartQuantities));
    };

    const handleBuynow = () => {
        if (oneproduct.is_variant === "true" && (!matchedCombination )) {
            toast.error("Please select all valid variant options before buying.");
            return;
        }

        const cart_id = localStorage.getItem("cart_id") ? Number(localStorage.getItem("cart_id")) : null;
        const customer_id = JSON.parse(localStorage.getItem("user"))?.id || null;

        const baseCartItem = {
            item_id: oneproduct.id,
            quantity,
            price: parseFloat(matchedCombination?.price || oneproduct.price),
            discount_price: parseFloat(matchedCombination?.discount_price || matchedCombination?.price || oneproduct.discount_price || oneproduct.price),
            ...(cart_id && { cart_id }),
            ...(customer_id && { customer_id }),
        };

        const cartItem = oneproduct.is_variant === "true" && matchedCombination
            ? {
                ...baseCartItem,
                variant_id: matchedCombination.id,
                selectedVariants: transformSelectedVariants(selectedAttributeValues)
            }
            : { ...baseCartItem, variant_id: null, selectedVariants: [] };

        dispatch(addCartItems(cartItem, toast, navigate, true));
    };

    const handleNotifyMe = () => {
        const userEmail = JSON.parse(localStorage.getItem('user'))?.email;
        if (!userEmail) {
            toast.error("User Email is not found. Please log in.");
            return;
        }

        if (oneproduct.is_variant === "true" && (!matchedCombination )) {
            toast.error("Please select a specific variant to be notified about.");
            return;
        }

        const payload = {
            product_id: id,
            variant_id: matchedCombination?.id || null,
            email: userEmail,
        };
        dispatch(sendNotifyRequest(payload, toast));
    };

    const handleAddToList = async (listName = "Default", product_id) => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const user_id = user?.id;
    
      const isVariant = oneproduct.is_variant === "true";
      const selected = isVariant && matchedCombination
        ? matchedCombination
        : {
            id: null,
            value: "Default",
            price: oneproduct.price ?? 0,
            discount_price: oneproduct.discount_price ?? oneproduct.price ?? 0,
            stock_quantity: oneproduct.stock_quantity ?? 0,
            stock_status: oneproduct.stock_status,
          };
    
      if (isVariant && (!matchedCombination )) {
        toast.error("Please select a valid variant to add to wishlist.");
        return;
      }
    
      let updatedWishlists = { ...userWishlists };
    
      // Create wishlist if not exists
      if (!updatedWishlists[listName]) {
        const res = await dispatch(createWishlist(user_id, listName));
        const newId = res?.data?.id;
    
        if (!newId) return toast.error("Failed to create wishlist");
    
        updatedWishlists[listName] = { id: newId, items: [] };
        setUserWishlists(updatedWishlists);
      }
    
      const wishlistId = updatedWishlists[listName].id;
      const items = updatedWishlists[listName]?.items || [];
    
      // Prepare selected variant payload (without price & discount_price)
      let selectedVariantsPayload = {};
      if (isVariant && matchedCombination) {
        Object.entries(selectedAttributeValues).forEach(([name, { id, value }]) => {
          selectedVariantsPayload[name.toLowerCase()] = {
            value,
            stock_quantity: matchedCombination.stock_quantity ?? 0,
            stock_status: matchedCombination.stock_status,
          };
        });
      }
    
      // Check for existing item
      const existingItem = items.find((item) =>
        String(item.product_id) === String(product_id) &&
        String(item.variant_id || "") === String(isVariant ? selected.id : "") &&
        String(item.wishlist_id) === String(wishlistId)
      );
    
      if (existingItem) {
        const newQty = (existingItem.quantity || 1) + 1;
        const updated = await dispatch(updateWishlistQuantity(existingItem.id, newQty, toast));
    
        if (updated?.success) {
          toast.success("Quantity updated in wishlist");
    
          const updatedItems = items.map((item) =>
            item.id === existingItem.id ? { ...item, quantity: newQty } : item
          );
          setUserWishlists((prev) => ({
            ...prev,
            [listName]: { ...prev[listName], items: updatedItems },
          }));
        } else {
          toast.error("Failed to update quantity");
        }
    
        setShowWishlistModalFor(null);
        return;
      }
    
      // Add new wishlist item
      const payload = {
        product_id,
        variant_id: isVariant ? selected.id : null,
        selected_variants: selectedVariantsPayload,
        price: selected.price ?? 0,
        discount_price: selected.discount_price ?? selected.price ?? 0,
      };
    
      const added = await dispatch(addToWishlist(wishlistId, payload, toast));
      if (!added?.success) {
        toast.error(added?.message || "Failed to add to wishlist");
        return;
      }
    
      // Refresh updated wishlists
      const refreshed = await dispatch(fetchUserWishlists(user_id, toast));
      const wishlistsArray = refreshed?.wishlists || refreshed?.data || [];
    
      const wishlistsByName = {};
      wishlistsArray.forEach((list) => {
        wishlistsByName[list.name] = {
          id: list.id,
          items: list.items || [],
        };
      });
    
      setUserWishlists(wishlistsByName);
      setShowWishlistModalFor(null);
    };

    const handleCreateNewList = (product_id) => {
        if (!newListName.trim()) return toast.error("List name required");
        if (userWishlists[newListName]) {
            toast.info("List already exists");
            return;
        }
        handleAddToList(newListName, product_id);
        setNewListName("");
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(linkToShare).then(() => {
            toast.success("Link copied to clipboard!");
        });
    };

    const shareOnFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(linkToShare)}`, "_blank");
    const shareOnTwitter = () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(linkToShare)}`, "_blank");
    const shareOnWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(linkToShare)}`, "_blank");
    const handleClickCart = () => {
        navigate(`/cart`);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    if (!oneproduct || singleProductIsLoading || loading) {
        return (
            <div className="animate-pulse text-center text-gray-600">
                Loading product details...
            </div>
        );
    }

    if (singleProductError) {
        return (
            <p className="text-red-500 text-center">There was an error loading the product.</p>
        );
    }

    const displayStockStatus = oneproduct.is_variant === "true"? (matchedCombination?.stock_status || "out_of_stock") : (oneproduct.stock_status || "out_of_stock");

    const selectedVariantTitle = oneproduct.is_variant === "true" && Object.keys(selectedAttributeValues).length > 0
        ? Object.values(selectedAttributeValues).map(val => val.value).join(" / ")
        : "";

    const disableButtons = isProductOutOfStock || (oneproduct.is_variant === "true" && (!matchedCombination ));
    return (
        <>
            <div className="mb-4">
                <StockBtn title={displayStockStatus === "in_stock" ? "In Stock" : "Out of Stock"} />
            </div>
            
            {showHurryMessage && !isProductOutOfStock && (
              <p className="text-red-600 font-semibold mt-2">
                Hurry up! Only {matchedCombination?.stock_quantity || oneproduct.stock_quantity} left in stock!
              </p>
            )}

            <div className="mb-4">
                <h1 className="text-2xl font-bold">
                    {name}
                    {selectedVariantTitle && <span> - {selectedVariantTitle}</span>}
                </h1>
                <p className="text-gray-600">{description}</p>

                <div className="flex items-center gap-2 text-[#00A762] font-medium pb-2">
                    {displayDiscountPrice > 0 && displayDiscountPrice < displayPrice ? (
                        <>
                            <span className="text-xs line-through text-gray-500">
                                ₹{displayPrice.toFixed(2)}
                            </span>
                            <span className="text-lg font-bold text-black">
                                ₹{displayDiscountPrice.toFixed(2)}
                            </span>
                            <span className="text-xs text-green-600 font-bold">
                                ({Math.round(((displayPrice - displayDiscountPrice) / displayPrice) * 100)} % off)
                            </span>
                        </>
                    ) : (
                        <span className="text-lg font-bold text-black">
                            ₹{displayPrice.toFixed(2)}
                        </span>
                    )}
                </div>
            </div>

            {oneproduct.is_variant === "true" && variant_value?.length > 0 && (
                <div className="space-y-6">
                    {Object.values(variant_name).map(variantTypeInfo => {
                      const matchedValues = variant_value.find(v => v.id === variantTypeInfo.id);
                      if (!matchedValues?.multiple_values.length) return null;
            
                      return (
                        <div key={variantTypeInfo.id}>
                          <h3 className="text-lg font-semibold mb-2 capitalize text-gray-800">
                            {variantTypeInfo.variant}
                          </h3>
                          <div className="flex gap-2 flex-wrap">
                            {matchedValues.multiple_values.map(value => {
                              const isOut = !variant_combinations.some(combo =>
                                combo.combination_attributes.some(attr =>
                                  attr.attribute_id.toString() === variantTypeInfo.id.toString() &&
                                  attr.value === value.value
                                ) && (combo.stock_quantity > 0 && combo.stock_status === "in_stock")
                              );
            
                              const isSelected =
                                selectedAttributeValues[variantTypeInfo.variant]?.value === value.value;
            
                              return (
                                <button
                                  key={value.id}
                                  onClick={() =>
                                    !isOut &&
                                    handleSelectValue(variantTypeInfo.variant, { id: value.id, value: value.value })
                                  }
                                  disabled={isOut}
                                  className={`px-4 py-2 rounded-md border text-sm font-medium transition-all
                                    ${isOut
                                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                      : isSelected
                                      ? "bg-green-100 text-green-700 border-green-500"
                                      : "bg-white hover:bg-gray-100 border-gray-300 text-gray-800"}`}
                                >
                                  {value.value}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                </div>
            )}
            
            <div className="my-8 flex items-center sm:justify-start justify-center">
                <div className="">
                    <h2 className="text-[#00A762] font-dm text-lg capitalize font-medium">
                        Quantity
                    </h2>
                </div>

                <div className="quantity_btn ml-4" name="quantity">
                    <button
                        className="btn_plus hover:bg-gray-200 hover:rounded-l-lg"
                        type="button"
                        onClick={handleIncrement}
                    >
                        <MdAdd className="text-[#00A762}" />
                    </button>
                    <input
                        className="w-full focus:outline-none text-center bg-transparent"
                        value={quantity}
                        disabled
                        style={{ border: "0px solid" }}
                    />
                    <button
                        className="btn_minus hover:bg-gray-200 hover:rounded-r-lg"
                        type="button"
                        onClick={handleDecrement}
                    >
                        <FiMinus style={{ color: "#00A762" }} />
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-12 gap-4 mb-8">
              <div className="md:col-span-6 col-span-12">
                {isProductOutOfStock ? (
                  <button
                    type="button"
                    onClick={handleNotifyMe}
                    className="flex items-center text-white bg-red-500 hover:bg-red-600 justify-center w-full rounded-lg shadow-md font-dm px-3 py-2 capitalize"
                  >
                    <svg
                      stroke="currentColor"
                      fill="none"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      className="mr-2 text-2xl"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 16h-1v-4h-1m1-4h.01M12 20c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z"
                      />
                    </svg>
                    Notify Me
                  </button>
                ) : isInCart ? (
                  <Btnoutline
                    title="go to cart"
                    width="100%"
                    handleClick={handleClickCart}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={disableButtons}
                    className={`flex items-center text-[#00A762] border justify-center w-full rounded-lg shadow-md font-dm px-3 py-2 capitalize ${
                      disableButtons
                        ? "cursor-not-allowed border-gray-400 opacity-50"
                        : "cursor-pointer border-[#00A762] hover:bg-[#E6F4EB]"
                    }`}
                  >
                    <svg
                      stroke="currentColor"
                      fill="none"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      className="text-[#00A762] mr-2 text-2xl"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      />
                    </svg>
                    Add to Cart
                  </button>
                )}
              </div>
            
              <div className="md:col-span-6 col-span-12">
                <button
                  onClick={handleBuynow}
                  disabled={disableButtons || isProductOutOfStock}
                  className={`w-full rounded-lg px-3 py-2 font-dm text-white shadow-md ${
                    disableButtons || isProductOutOfStock
                      ? "bg-gray-400 cursor-not-allowed opacity-50"
                      : "bg-[#00A762] hover:bg-green-700"
                  }`}
                >
                  Buy Now
                </button>
              </div>
            </div>


            <div className="mt-8 relative">
                <button
                    onClick={() => {
                        const accessToken = localStorage.getItem("accessToken");
                        if (!accessToken) {
                            navigate("/login");
                            toast.error("You need to log in to manage your wishlist");
                            return;
                        }
                        setShowWishlistModalFor(oneproduct.id);
                    }}
                    className="w-full py-2 rounded-lg border border-gray-400 flex items-center justify-center text-lg font-bold hover:bg-gray-100 transition-colors"
                >
                    {wishlist.has(oneproduct.id) ? (
                        <FaHeart className="text-red-500 text-[22px] mr-2" />
                    ) : (
                        <FaRegHeart className="text-gray-500 text-[22px] mr-2" />
                    )}
                    Add to Wishlist
                </button>

                {showWishlistModalFor === oneproduct.id && (
                    <div className="absolute z-50 bg-white border border-gray-200 shadow-lg rounded-xl p-4 top-14 left-0 w-72">
                        <h3 className="font-semibold text-lg text-gray-800 mb-3">
                            Save to Wishlist
                        </h3>

                        <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                            {Object.keys(userWishlists).length > 0 ? (
                                Object.keys(userWishlists).map((listName) => (
                                    <button
                                        key={listName}
                                        onClick={() => handleAddToList(listName, oneproduct.id)}
                                        className="w-full text-left bg-gray-100 p-2 rounded-md hover:bg-gray-200 transition"
                                    >
                                        ➕ {listName}
                                    </button>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500">No lists found.</p>
                            )}
                        </div>

                        <div className="border-t my-3" />

                        <div className="space-y-2">
                            <input
                                type="text"
                                placeholder="New list name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                value={newListName}
                                onChange={(e) => setNewListName(e.target.value)}
                            />
                            <button
                                onClick={() => handleCreateNewList(oneproduct.id)}
                                className="w-full bg-green-600 hover:bg-green-700 text-white text-sm py-2 rounded-md transition"
                            >
                                ➕ Create & Add
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between mt-8">
                <div className="">
                    <h2 className="text-[#00A762] font-dm text-lg capitalize font-medium">
                        Share on :
                    </h2>
                </div>

                <div className="flex items-center justify-center sm:gap-6 gap-2">
                    <div
                        className="rounded-full bg-[#808080] h-10 w-10 flex items-center justify-center cursor-pointer"
                        onClick={copyToClipboard}
                    >
                        <FaLink className="text-white text-xl" />
                    </div>

                    <div
                        className="rounded-full bg-[#1877F2] h-10 w-10 flex items-center justify-center cursor-pointer"
                        onClick={shareOnFacebook}
                    >
                        <FaFacebookF className="text-white text-xl" />
                    </div>

                    <div
                        className="rounded-full bg-green-500 h-10 w-10 flex items-center justify-center cursor-pointer"
                        onClick={shareOnWhatsApp}
                    >
                        <FaWhatsapp className="text-white text-2xl" />
                    </div>

                    <div
                        className="rounded-full bg-[#1DA1F2] h-10 w-10 flex items-center justify-center cursor-pointer"
                        onClick={shareOnTwitter}
                    >
                        <FaTwitter className="text-white text-xl" />
                    </div>
                </div>
            </div>
            <ProductRatings title="4" id={id} />
        </>
    );
};

export default ProductSingleContentPage;